import React, { forwardRef, useState } from "react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorEvents } from "@tiptap/core";
import {
  MenuButtonBulletedList,
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuButtonUnderline,
  MenuButtonOrderedList,
  MenuButtonStrikethrough,
  RichTextEditor,
  RichTextEditorProps,
  type RichTextEditorRef,
  MenuDivider,
} from "mui-tiptap";
import { Box } from "@mui/system";
import Typography from "../Typography";
import useStyles from "./useStyles";

export interface RichTextFieldProps
  extends Omit<RichTextEditorProps, "extensions"> {
  value?: string;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  onChange?: (value: string) => void;
}

const RichTextField = forwardRef<RichTextEditorRef, RichTextFieldProps>(
  (
    {
      value = "",
      error = false,
      helperText,
      placeholder,
      label,
      required,
      onBlur,
      onChange,
    },
    ref,
  ) => {
    const { helperTextRoot, wrapper, labelContainer } = useStyles();
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = (newContent: string) => {
      if (onChange) {
        onChange(newContent);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = (props: EditorEvents["blur"]) => {
      setTimeout(() => {
        setIsFocused(false);
      }, 300);
      if (onBlur) {
        onBlur(props);
      }
    };

    return (
      <Box className={wrapper}>
        <Box className={labelContainer}>
          <Typography
            variant="caption"
            // eslint-disable-next-line no-nested-ternary
            color={error ? "error" : isFocused ? "primary" : "text.secondary"}
            id="rich-text-label"
          >
            {label} {!!required && "*"}
          </Typography>
        </Box>
        <RichTextEditor
          ref={ref}
          extensions={[
            StarterKit,
            Placeholder.configure({
              placeholder,
            }),
          ]}
          content={value}
          onUpdate={({ editor }) => {
            handleChange(editor.getHTML());
          }}
          RichTextFieldProps={{
            color: error ? "error" : "primary",
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          sx={({ palette }) => ({
            "& .ProseMirror": {
              paddingTop: 1.5,
              paddingBottom: 1.5,
              paddingLeft: 2,
              paddingRight: 2,
            },
            "& .is-editor-empty::before": {
              color: "#000000 !important",
              opacity: 0.38,
            },
            "& .MuiTiptap-FieldContainer-notchedOutline": {
              ...(error
                ? { borderColor: `${palette.error.main} !important` }
                : {}),
            },
            "& .MuiTiptap-RichTextField-menuBar": {
              backgroundColor: "white",
              padding: 1,
              borderBottomColor: "#C4C4C4",
            },
          })}
          shouldRerenderOnTransaction
          renderControls={() => (
            <MenuControlsContainer>
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuButtonStrikethrough />
              <MenuDivider />
              <MenuButtonOrderedList />
              <MenuButtonBulletedList />
            </MenuControlsContainer>
          )}
        />
        {helperText && (
          <Typography
            variant="caption"
            color="error"
            className={helperTextRoot}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

export default RichTextField;
